import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PhoneIncoming, Mic, Play, AlertTriangle, ShieldCheck } from "lucide-react";

// Advisor dashboard panel: INBOUND call recordings the signed-in advisor owns (incl. UNLINKED inbound,
// which are intentionally not tied to a household — caller ID never links identity). Playback reuses the
// authenticated blob endpoint; the server re-checks owner/org/admin authorization on every request.
const STATUS_LABEL = {
  saved: "Saved", saved_simulated: "Saved (simulated)", save_failed: "Save failed",
  record_active: "Recording", consumer_declined: "Consumer declined", consumer_timeout: "No consumer response",
  advisor_declined: "Advisor declined", advisor_timeout: "No advisor response",
  recording_start_failed: "Recording failed to start",
};

const fmtDur = (s) => (s == null ? null : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);
const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export const InboundRecordings = () => {
  const [recs, setRecs] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/telephony/recordings/mine");
        setRecs((data.recordings || []).filter((r) => r.direction === "inbound"));
      } catch { setLoadErr(true); setRecs([]); }
    })();
  }, []);

  const play = async (r) => {
    setErr(null);
    try {
      const res = await api.get(`/telephony/recordings/${r.id}/audio`, { responseType: "blob" });
      setPlayingUrl(URL.createObjectURL(res.data));
      setPlayingId(r.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Recording unavailable.");
    }
  };

  // Always render the panel for authorized advisors/admins so the feature is discoverable with explicit
  // loading / empty / error states (never a silent null that looks like a missing feature).
  if (recs === null) {
    return (
      <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden" data-testid="inbound-recordings">
        <div className="flex items-center gap-2 px-4 py-3 bg-[#F8F8F5] border-b">
          <PhoneIncoming className="w-4 h-4 text-[#1E3E62]" />
          <h2 className="text-sm font-bold text-[#0B192C]">Inbound call recordings</h2>
        </div>
        <p className="px-4 py-3 text-xs text-[#9CA3AF]" data-testid="inbound-rec-loading">Loading inbound call recordings…</p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden" data-testid="inbound-recordings">
      <div className="flex items-center gap-2 px-4 py-3 bg-[#F8F8F5] border-b">
        <PhoneIncoming className="w-4 h-4 text-[#1E3E62]" />
        <h2 className="text-sm font-bold text-[#0B192C]">Inbound call recordings</h2>
        <span className="text-xs text-[#6B7280]">({recs.length})</span>
      </div>
      {loadErr && <p className="px-4 py-3 text-xs text-[#B91C1C]" data-testid="inbound-rec-loaderror">Couldn't load your inbound call recordings. Please try again shortly.</p>}
      {!loadErr && recs.length === 0 && <p className="px-4 py-3 text-xs text-[#9CA3AF]" data-testid="inbound-rec-empty">No inbound call recordings yet. Recorded inbound calls you handle will appear here.</p>}
      <ul className="divide-y">
        {recs.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`inbound-rec-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#0B192C] flex items-center gap-2">
                <Mic className="w-3.5 h-3.5 text-[#1E3E62]" />
                {STATUS_LABEL[r.status] || r.status}
                {fmtDur(r.duration_sec) && <span className="text-xs font-normal text-[#6B7280]">· {fmtDur(r.duration_sec)}</span>}
              </p>
              <p className="text-xs text-[#9CA3AF] mt-0.5">
                {fmtWhen(r.created_at)}
                {!r.linked && <span className="ml-2 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#EEF2F6] text-[#1E3E62]">not linked to a household</span>}
              </p>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                <ShieldCheck className="w-3 h-3 inline mr-1 text-[#047857]" />
                consent: advisor {r.advisor_consent ? "✓" : "—"} · consumer {r.consumer_consent ? "✓" : "—"}
              </p>
              {r.failure && <p className="text-[11px] text-[#B91C1C] flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" /> recording unavailable</p>}
            </div>
            {r.has_audio ? (
              <Button size="sm" variant="outline" onClick={() => play(r)} data-testid={`inbound-rec-play-${r.id}`}
                className="text-[11px] h-7 px-2 rounded-full shrink-0"><Play className="w-3 h-3 mr-1" /> Play</Button>
            ) : (
              <span className="text-[#94A3B8] text-[11px] shrink-0" data-testid={`inbound-rec-noaudio-${r.id}`}>no audio</span>
            )}
          </li>
        ))}
      </ul>
      {err && <p className="px-4 py-2 text-xs text-[#B91C1C]" data-testid="inbound-rec-error">{err}</p>}
      {playingUrl && (
        <div className="px-4 pb-3">
          <audio controls autoPlay src={playingUrl} className="w-full h-8" data-testid="inbound-rec-audio" />
        </div>
      )}
    </div>
  );
};
